import { Link } from '@inertiajs/react'
import { ExternalLink } from 'lucide-react'

import AppLayout from '@/layouts/app-layout'

interface DomainRow {
  hostname: string
  status: string
}

interface SiteRow {
  uid: string
  name: string
  business_name: string | null
  status: string
  latest_deploy_status: string | null
  preview_url: string | null
  domains: DomainRow[]
  content_versions: number
}

interface Props {
  sites: SiteRow[]
}

const deployTone: Record<string, string> = {
  published: 'bg-primary/10 text-primary',
  built: 'bg-primary/10 text-primary',
  building: 'bg-amber-100 text-amber-800',
  queued: 'bg-muted text-muted-foreground',
  failed: 'bg-red-100 text-red-800',
}

export default function Index({ sites }: Props) {
  return (
    <AppLayout title="Sites">
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/50 text-left">
              <th className="px-4 py-3 text-[13px] font-semibold">Site</th>
              <th className="px-4 py-3 text-[13px] font-semibold">Build</th>
              <th className="px-4 py-3 text-[13px] font-semibold">Domains</th>
              <th className="px-4 py-3 text-[13px] font-semibold">Content</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {sites.map((site) => (
              <tr key={site.uid} className="border-b last:border-b-0 hover:bg-muted/30">
                <td className="px-4 py-3 align-top">
                  <div className="font-medium">{site.name}</div>
                  {site.business_name && (
                    <div className="text-muted-foreground text-[13px]">{site.business_name}</div>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  {site.latest_deploy_status ? (
                    <span
                      className={`inline-flex rounded-md px-2 py-0.5 text-xs font-medium ${deployTone[site.latest_deploy_status] ?? 'bg-muted text-muted-foreground'}`}
                    >
                      {site.latest_deploy_status}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  {site.domains.length === 0 && <span className="text-muted-foreground">—</span>}
                  {site.domains.map((d) => (
                    <div key={d.hostname} className="text-[13px]">
                      {d.hostname}{' '}
                      <span className="text-muted-foreground">({d.status})</span>
                    </div>
                  ))}
                </td>
                <td className="px-4 py-3 align-top" data-numeric>
                  {site.content_versions} version{site.content_versions === 1 ? '' : 's'}
                </td>
                <td className="px-4 py-3 text-right align-top">
                  <div className="flex justify-end gap-3 text-[13px]">
                    <Link className="text-primary font-medium hover:underline" href={`/sites/${site.uid}/content`}>
                      Edit content
                    </Link>
                    <Link
                      className="text-primary font-medium hover:underline"
                      href={`/sites/${site.uid}/change_requests`}
                    >
                      Change requests
                    </Link>
                    {site.preview_url && (
                      <a
                        className="text-primary inline-flex items-center gap-1 font-medium hover:underline"
                        href={site.preview_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Preview
                        <ExternalLink className="size-3" />
                      </a>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {sites.length === 0 && (
          <p className="text-muted-foreground px-4 py-8 text-center">
            No sites yet — they appear when website orders arrive.
          </p>
        )}
      </div>
    </AppLayout>
  )
}
